import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { Components } from 'react-markdown';
import { autoTune } from '../services/api';
import { useOptimizationStore, ChatMessage } from '../store/optimizationStore';
import { useAuthContext } from '../contexts/AuthContext';

// Используем ChatMessage из store
type Message = ChatMessage;

interface DialogMessage {
  type: 'human' | 'ai';
  content: string;
}

interface MultiAgentChatComparisonProps {
  onShowLoginModal?: () => void;
}

const MultiAgentChatComparison: React.FC<MultiAgentChatComparisonProps> = ({ onShowLoginModal }) => {
  const [isLoadingBefore, setIsLoadingBefore] = useState(false);
  const [isLoadingAfter, setIsLoadingAfter] = useState(false);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());

  const beforeChatRef = useRef<HTMLDivElement>(null);
  const afterChatRef = useRef<HTMLDivElement>(null);

  // Селективно подписываемся только на нужные значения
  const {
    coordinatorPrompt,
    coordinatorModel,
    coordinatorTemperature,
    workers,
    multiAgentIterationResults,
    multiAgentIsOptimized,
    multiAgentSelectedIterationIndex,
    setMultiAgentSelectedIterationIndex,
    // Чат из store
    multiAgentChatMessages,
    multiAgentChatInputs,
    setMultiAgentChatInputs,
    setMultiAgentChatInput,
    addMultiAgentChatMessage,
    setMultiAgentChatMessages
  } = useOptimizationStore();
  
  // Используем чаты из store
  const beforeMessages = multiAgentChatMessages.before;
  const afterMessages = multiAgentChatMessages.after;
  const beforeInput = multiAgentChatInputs.before;
  const afterInput = multiAgentChatInputs.after;

  const { isAuthenticated } = useAuthContext();

  // Refs for auto-growing textareas
  const beforeInputRef = useRef<HTMLTextAreaElement>(null);
  const afterInputRef = useRef<HTMLTextAreaElement>(null);

  const decodeNewlines = (text: string) => {
    return text.replace(/\\n/g, '\n');
  };

  // Экранируем маркеры списков в начале строк, чтобы символы "+" и "-" отображались как текст
  const escapeMarkdownListMarkers = (text: string) => {
    // Экранируем маркеры списков в начале строк, если после символа пробел или конец строки
    // Не трогаем случаи типа "+1" или "-3"
    return text.replace(/^(\s{0,3})([+\-\*])(?=\s|$)/gm, '$1\\$2');
  };

  const scrollToBottom = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom(beforeChatRef);
  }, [beforeMessages]);

  useEffect(() => {
    scrollToBottom(afterChatRef);
  }, [afterMessages]);

  // Auto-resize textareas on value change
  useEffect(() => {
    const el = beforeInputRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    }
  }, [beforeInput]);

  useEffect(() => {
    const el = afterInputRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    }
  }, [afterInput]);

  // Обработчик событий удален - очистка чатов теперь происходит через store

  // Проверяем, есть ли хотя бы одна итерация для работы чата
  const optimizedIterationResults = useMemo(() => {
    // Фильтруем только оптимизированные результаты (seq > 0 или iteration > 0), исключая baseline (seq 0)
    return multiAgentIterationResults
      .filter(result => {
        const seq = result.seq !== undefined ? result.seq : result.iteration;
        return seq !== 0 && seq !== undefined;
      })
      .sort((a, b) => {
        const seqA = a.seq !== undefined ? a.seq : a.iteration;
        const seqB = b.seq !== undefined ? b.seq : b.iteration;
        return (seqA || 0) - (seqB || 0);
      });
  }, [multiAgentIterationResults]);

  const hasIterationResults = optimizedIterationResults.length > 0;

  // ⚠️ Автофокус убран - фокус устанавливается только при явном взаимодействии пользователя (отправка сообщения)

  const getCurrentIterationResult = () => {
    if (optimizedIterationResults.length > 0 && multiAgentSelectedIterationIndex < optimizedIterationResults.length) {
      return optimizedIterationResults[multiAgentSelectedIterationIndex];
    }
    return null;
  };

  const createChatPrompt = (basePrompt: string) => {
    return `You are an AI assistant. Follow these instructions carefully:

${basePrompt}

IMPORTANT: Always follow the instructions above when responding to user messages. Your responses should be based on and consistent with these instructions.`;
  };

  const handleSendBefore = useCallback(async () => {
    if (!isAuthenticated) {
      if (onShowLoginModal) {
        onShowLoginModal();
      } else {
        alert('Чтобы писать в чат, войдите в систему.');
      }
      return;
    }
    if (isLoadingBefore) return;

    const inputText = beforeInputRef.current?.value ?? beforeInput;
    if (inputText.trim() && coordinatorPrompt) {
      const userMessage: Message = { id: Date.now(), text: inputText, sender: 'user' };
      addMultiAgentChatMessage('before', userMessage);
      setMultiAgentChatInput('before', '');
      // Вернуть фокус на поле ввода левого чата
      setTimeout(() => beforeInputRef.current?.focus(), 0);
      setIsLoadingBefore(true);

      try {
        const dialog: DialogMessage[] = beforeMessages.map(msg => {
          if (msg.sender === 'user') {
            return { type: 'human' as const, content: msg.text };
          } else {
            const dialogMsg: DialogMessage = { type: 'ai' as const, content: msg.text };
            
            if (msg.intermediateMessages && msg.intermediateMessages.length > 0) {
              const intermediateContent = msg.intermediateMessages
                .map(im => `[${im.name}]: ${im.content}`)
                .join('\n\n');
              dialogMsg.content = `${dialogMsg.content}\n\n--- Intermediate Messages ---\n${intermediateContent}`;
            }
            
            return dialogMsg;
          }
        });
        dialog.push({ type: 'human', content: inputText });

        // Используем мультиагентную систему для левого чата
        const modelData = {
          "master-agent": {
            name: "master",
            prompt: coordinatorPrompt,
            model_name: coordinatorModel,
            temperature: coordinatorTemperature
          },
          "worker-agents": workers.map((worker, index) => ({
            name: worker.name || `Worker ${index + 1}`,
            prompt: worker.prompt,
            model_name: worker.model,
            temperature: worker.temperature
          }))
        };
        
        const response = await autoTune.multiAgentChat({
          model: modelData,
          dialog
        });

        const replyData = response.data?.reply;
        
        // Создаем маппинг имен для мультиагентной системы
        const agentNameMapping: { [key: string]: string } = {};
        
        // Маппинг для master-agent
        agentNameMapping['master'] = 'Координатор';
        
        // Маппинг для worker агентов
        workers.forEach((worker, index) => {
          agentNameMapping[worker.name] = worker.name;
          agentNameMapping[`worker${index + 1}`] = worker.name;
          agentNameMapping[`worker-${index + 1}`] = worker.name;
        });
        
        // Обновляем имена в intermediate_messages
        const updatedIntermediateMessages = replyData?.intermediate_messages?.map((msg: any) => ({
          ...msg,
          name: agentNameMapping[msg.name] || msg.name
        })) || [];
        
        const botMessage: Message = { 
          id: Date.now(), 
          text: replyData?.output?.content || response.data?.reply || response.data?.response || 'Не удалось получить ответ', 
          sender: 'bot',
          intermediateMessages: updatedIntermediateMessages
        };
        addMultiAgentChatMessage('before', botMessage);
      } catch (error) {
        console.error('Chat failed:', error);
        const errorMessage: Message = { id: Date.now(), text: 'Произошла ошибка. Попробуйте ещё раз.', sender: 'bot' };
        addMultiAgentChatMessage('before', errorMessage);
      } finally {
        setIsLoadingBefore(false);
        // Снова установить фокус после завершения запроса
        setTimeout(() => beforeInputRef.current?.focus(), 0);
      }
    }
  }, [isAuthenticated, onShowLoginModal, isLoadingBefore, beforeInput, coordinatorPrompt, beforeMessages, coordinatorModel, coordinatorTemperature, workers, addMultiAgentChatMessage, setMultiAgentChatInput]);

  const handleSendAfter = useCallback(async () => {
    if (!isAuthenticated) {
      if (onShowLoginModal) {
        onShowLoginModal();
      } else {
        alert('Для отправки сообщений нужно войти в систему.');
      }
      return;
    }
    if (isLoadingAfter) return;

    const currentResult = getCurrentIterationResult();
    const inputText = afterInputRef.current?.value ?? afterInput;
    if (inputText.trim() && currentResult && hasIterationResults) {
      const userMessage: Message = { id: Date.now(), text: inputText, sender: 'user' };
      addMultiAgentChatMessage('after', userMessage);
      setMultiAgentChatInput('after', '');
      // Вернуть фокус на поле ввода правого чата
      setTimeout(() => afterInputRef.current?.focus(), 0);
      setIsLoadingAfter(true);

      try {
        const dialog: DialogMessage[] = afterMessages.map(msg => {
          if (msg.sender === 'user') {
            return { type: 'human' as const, content: msg.text };
          } else {
            const dialogMsg: DialogMessage = { type: 'ai' as const, content: msg.text };
            
            if (msg.intermediateMessages && msg.intermediateMessages.length > 0) {
              const intermediateContent = msg.intermediateMessages
                .map(im => `[${im.name}]: ${im.content}`)
                .join('\n\n');
              dialogMsg.content = `${dialogMsg.content}\n\n--- Intermediate Messages ---\n${intermediateContent}`;
            }
            
            return dialogMsg;
          }
        });
        dialog.push({ type: 'human' as const, content: inputText });

        // Формируем worker-agents: используем данные из currentResult, если они есть и валидны, иначе fallback на workers
        const resultWorkerAgents = currentResult["worker-agents"];
        const workerAgentsData = (resultWorkerAgents && Array.isArray(resultWorkerAgents) && resultWorkerAgents.length > 0)
          ? resultWorkerAgents.map((worker: any, index: number) => ({
              name: worker.name || workers[index]?.name || `Worker ${index + 1}`,
              prompt: worker.prompt || workers[index]?.prompt || '',
              model_name: worker.model_name || workers[index]?.model || coordinatorModel,
              temperature: worker.temperature !== undefined ? worker.temperature : (workers[index]?.temperature ?? coordinatorTemperature)
            }))
          : workers.map((worker, index) => ({
              name: worker.name || `Worker ${index + 1}`,
              prompt: worker.prompt || '',
              model_name: worker.model || coordinatorModel,
              temperature: worker.temperature ?? coordinatorTemperature
            }));

        const modelData = {
          "master-agent": {
            name: "master",
            prompt: currentResult["master-agent"]?.prompt || coordinatorPrompt || '',
            model_name: currentResult["master-agent"]?.model_name || coordinatorModel || 'gpt-4.1-mini',
            temperature: currentResult["master-agent"]?.temperature !== undefined ? currentResult["master-agent"].temperature : (coordinatorTemperature ?? 0.8)
          },
          "worker-agents": workerAgentsData
        };

        // Валидация: проверяем, что все обязательные поля присутствуют
        if (!modelData["master-agent"].prompt) {
          throw new Error('Нужен промпт главного агента');
        }
        if (!modelData["master-agent"].model_name) {
          throw new Error('Master agent model_name is required');
        }
        if (workerAgentsData.length === 0) {
          throw new Error('Нужен хотя бы один агент-исполнитель');
        }
        workerAgentsData.forEach((worker, index) => {
          if (!worker.prompt) {
            throw new Error(`Worker agent ${index + 1} prompt is required`);
          }
          if (!worker.model_name) {
            throw new Error(`Worker agent ${index + 1} model_name is required`);
          }
        });

        console.log('📤 Multi-agent after-chat request:', { model: modelData, dialog });

        const response = await autoTune.multiAgentChat({
          model: modelData,
          dialog
        });

        const replyData = response.data?.reply;
        
        // Создаем маппинг между именами из API и кастомными именами
        const agentNameMapping: { [key: string]: string } = {};
        
        // Добавляем маппинг для master-agent
        agentNameMapping['master'] = 'Координатор';
        
        // Добавляем маппинг для worker агентов
        if (currentResult["worker-agents"]) {
          currentResult["worker-agents"].forEach((apiAgent: any, index: number) => {
            const customAgent = workers[index];
            if (customAgent && apiAgent.name) {
              // Маппим как по оригинальному имени, так и по возможным вариантам
              agentNameMapping[apiAgent.name] = customAgent.name;
              agentNameMapping[`worker${index + 1}`] = customAgent.name;
              agentNameMapping[`worker-${index + 1}`] = customAgent.name;
            }
          });
        }
        
        // Также добавляем маппинг по индексу на случай, если API использует числовые имена
        workers.forEach((worker, index) => {
          agentNameMapping[`worker${index + 1}`] = worker.name;
          agentNameMapping[`worker-${index + 1}`] = worker.name;
        });
        
        console.log('🔍 Agent name mapping:', agentNameMapping);
        console.log('🔍 API intermediate messages:', replyData?.intermediate_messages);
        
        // Обновляем имена в intermediate_messages
        const updatedIntermediateMessages = replyData?.intermediate_messages?.map((msg: any) => ({
          ...msg,
          name: agentNameMapping[msg.name] || msg.name
        })) || [];
        
        const botMessage: Message = { 
          id: Date.now(), 
          text: replyData?.output?.content || response.data?.reply || response.data?.response || 'Не удалось получить ответ', 
          sender: 'bot',
          intermediateMessages: updatedIntermediateMessages
        };
        addMultiAgentChatMessage('after', botMessage);
      } catch (error) {
        console.error('Multi-agent chat failed:', error);
        const errorMessage: Message = { id: Date.now(), text: 'Произошла ошибка. Попробуйте ещё раз.', sender: 'bot' };
        addMultiAgentChatMessage('after', errorMessage);
      } finally {
        setIsLoadingAfter(false);
        // Снова установить фокус после завершения запроса
        setTimeout(() => afterInputRef.current?.focus(), 0);
      }
    }
  }, [isAuthenticated, onShowLoginModal, isLoadingAfter, afterInput, afterMessages, optimizedIterationResults, multiAgentSelectedIterationIndex, coordinatorPrompt, coordinatorModel, coordinatorTemperature, workers, hasIterationResults, addMultiAgentChatMessage, setMultiAgentChatInput]);

  const handleModelSelect = useCallback((iterationIndex: number) => {
    setMultiAgentSelectedIterationIndex(iterationIndex);
    setShowModelSelector(false);
  }, [setMultiAgentSelectedIterationIndex]);

  const handleClearBeforeChat = () => {
    setMultiAgentChatMessages({
      ...multiAgentChatMessages,
      before: []
    });
    setMultiAgentChatInput('before', '');
  };

  const handleClearAfterChat = () => {
    setMultiAgentChatMessages({
      ...multiAgentChatMessages,
      after: []
    });
    setMultiAgentChatInput('after', '');
  };

  const handleClearAllChats = () => {
    setMultiAgentChatMessages({
      before: [],
      after: []
    });
    setMultiAgentChatInputs({
      before: '',
      after: ''
    });
  };

  const toggleIntermediateMessages = useCallback(
    (messageId: number, chatType: 'before' | 'after') => {
      setExpandedMessages(prev => {
        const newSet = new Set(prev);
        const wasExpanded = newSet.has(messageId);

        if (wasExpanded) {
          newSet.delete(messageId);
        } else {
          newSet.add(messageId);
          // Важно: НЕ автопрокручиваем чат при раскрытии reasoning,
          // чтобы не "выкидывать" пользователя из текущего контекста чтения.
        }

        return newSet;
      });
    },
    []
  ); // Пустой массив зависимостей, так как функция не зависит от внешних значений


  // Important: keep component identities stable across renders.
  // Otherwise expanding/collapsing reasoning can remount the message list and reset scroll position.
  const MarkdownRenderer: React.FC<{ children: string }> = useMemo(() => {
    const C = React.memo(({ children }: { children: string }) => {
      const components: Components = useMemo(() => ({
      h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
      h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
      h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
      h4: ({ children }) => <h4 className="markdown-h4">{children}</h4>,
      h5: ({ children }) => <h5 className="markdown-h5">{children}</h5>,
      h6: ({ children }) => <h6 className="markdown-h6">{children}</h6>,
      p: ({ children }) => <p className="markdown-p">{children}</p>,
      ul: ({ children, className }) => {
        const isTaskList = className?.includes('contains-task-list');
        return (
          <ul className={`markdown-ul ${isTaskList ? 'markdown-task-list' : ''}`}>
            {children}
          </ul>
        );
      },
      ol: ({ children }) => <ol className="markdown-ol">{children}</ol>,
      li: ({ children, className }) => {
        const isTaskItem = className?.includes('task-list-item');
        if (isTaskItem) {
          const childrenArray = React.Children.toArray(children);
          const checkbox = childrenArray[0] as React.ReactElement;
          const text = childrenArray.slice(1);
          const isChecked = checkbox?.props?.checked;
          
          return (
            <li className="markdown-task-list-item">
              <input 
                type="checkbox" 
                checked={isChecked} 
                readOnly 
                className="markdown-checkbox"
              />
              <span className={isChecked ? 'markdown-task-completed' : ''}>
                {text}
              </span>
            </li>
          );
        }
        return <li className="markdown-li">{children}</li>;
      },
      blockquote: ({ children }) => <blockquote className="markdown-blockquote">{children}</blockquote>,
      code: ({ className, children, ...props }) => {
        const match = /language-(\w+)/.exec(className || '');
        const isInline = !match;
        
        return !isInline ? (
          <pre className="markdown-pre" data-language={match?.[1]}>
            <code className={`markdown-code language-${match?.[1]}`} {...props}>
              {children}
            </code>
          </pre>
        ) : (
          <code className="markdown-inline-code" {...props}>
            {children}
          </code>
        );
      },
      table: ({ children }) => <table className="markdown-table">{children}</table>,
      thead: ({ children }) => <thead className="markdown-thead">{children}</thead>,
      tbody: ({ children }) => <tbody className="markdown-tbody">{children}</tbody>,
      tr: ({ children }) => <tr className="markdown-tr">{children}</tr>,
      th: ({ children }) => <th className="markdown-th">{children}</th>,
      td: ({ children }) => <td className="markdown-td">{children}</td>,
      a: ({ children, href }) => (
        <a href={href} className="markdown-link" target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
      strong: ({ children }) => <strong className="markdown-strong">{children}</strong>,
      em: ({ children }) => <em className="markdown-em">{children}</em>,
      hr: () => <hr className="markdown-hr" />,
      img: ({ src, alt }) => <img src={src} alt={alt} className="markdown-img" />,
      del: ({ children }) => <del className="markdown-del">{children}</del>,
      ins: ({ children }) => <ins className="markdown-ins">{children}</ins>
    }), []);

      const decodedChildren = useMemo(() => escapeMarkdownListMarkers(decodeNewlines(children)), [children]);

      return (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeHighlight]}
          components={components}
        >
          {decodedChildren}
        </ReactMarkdown>
      );
    }, (prevProps, nextProps) => prevProps.children === nextProps.children);

    C.displayName = 'MarkdownRenderer';
    return C;
  }, []);

  const IntermediateMessagesRenderer: React.FC<{
    messages: Array<{
      type: 'ai';
      content: string;
      name: string;
      id: string;
    }>;
    isExpanded: boolean;
  }> = useMemo(() => {
    const C = React.memo(
      ({ messages, isExpanded }: { messages: Array<{ type: 'ai'; content: string; name: string; id: string }>; isExpanded: boolean }) => {
        // Всегда рендерим контейнер, но скрываем через CSS для плавной анимации
        if (!messages.length) return null;

        return (
          <div className={`intermediate-messages ${isExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="intermediate-header">
              <i className="fas fa-cogs"></i>
              <span>Ход рассуждений агентов</span>
            </div>
            <div className="intermediate-content">
              {messages.map((msg, index) => (
                <div key={msg.id || index} className="intermediate-message">
                  <div className="intermediate-agent-name">
                    <i className="fas fa-robot"></i>
                    {msg.name}
                  </div>
                  <div className="intermediate-message-content">
                    <MarkdownRenderer>{msg.content}</MarkdownRenderer>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      },
      (prevProps, nextProps) =>
        prevProps.isExpanded === nextProps.isExpanded &&
        prevProps.messages.length === nextProps.messages.length &&
        prevProps.messages.every((msg, index) => {
          const nextMsg = nextProps.messages[index];
          return msg.id === nextMsg.id && msg.content === nextMsg.content && msg.name === nextMsg.name;
        })
    );

    C.displayName = 'IntermediateMessagesRenderer';
    return C;
  }, [MarkdownRenderer]);

  const ChatMessage: React.FC<{
    message: Message;
    isExpanded: boolean;
    onToggleExpanded: (messageId: number) => void;
  }> = useMemo(() => {
    const C = React.memo(
      ({ message, isExpanded, onToggleExpanded }: { message: Message; isExpanded: boolean; onToggleExpanded: (messageId: number) => void }) => {
        const handleToggleExpanded = useCallback(() => {
          onToggleExpanded(message.id);
        }, [onToggleExpanded, message.id]);

        return (
          <div className={`chat-message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}>
            <MarkdownRenderer>{message.text}</MarkdownRenderer>
            {message.sender === 'bot' && message.intermediateMessages && message.intermediateMessages.length > 0 && (
              <div className="intermediate-toggle-container">
                <button className="intermediate-toggle-btn" onClick={handleToggleExpanded}>
                  <i className={`fas fa-${isExpanded ? 'eye-slash' : 'eye'}`}></i>
                  {isExpanded ? 'Скрыть' : 'Показать'} рассуждения
                </button>
                <IntermediateMessagesRenderer messages={message.intermediateMessages} isExpanded={isExpanded} />
              </div>
            )}
          </div>
        );
      },
      (prevProps, nextProps) => {
        // Перерендериваем только если изменились релевантные пропсы
        return (
          prevProps.message.id === nextProps.message.id &&
          prevProps.message.text === nextProps.message.text &&
          prevProps.message.sender === nextProps.message.sender &&
          prevProps.isExpanded === nextProps.isExpanded &&
          prevProps.message.intermediateMessages?.length === nextProps.message.intermediateMessages?.length &&
          (prevProps.message.intermediateMessages || []).every((msg, index) => {
            const nextMsg = nextProps.message.intermediateMessages?.[index];
            return msg.id === nextMsg?.id && msg.content === nextMsg?.content && msg.name === nextMsg?.name;
          })
        );
      }
    );

    C.displayName = 'ChatMessage';
    return C;
  }, [MarkdownRenderer, IntermediateMessagesRenderer]);

  // Мемоизируем рендеринг сообщений
  const beforeMessagesElements = useMemo(() => 
    beforeMessages.map((message) => (
      <ChatMessage
        key={message.id}
        message={message}
        isExpanded={expandedMessages.has(message.id)}
        onToggleExpanded={(id) => toggleIntermediateMessages(id, 'before')}
      />
    )), [beforeMessages, expandedMessages] // Убрали toggleIntermediateMessages из зависимостей
  );

  const afterMessagesElements = useMemo(() => 
    afterMessages.map((message) => (
      <ChatMessage
        key={message.id}
        message={message}
        isExpanded={expandedMessages.has(message.id)}
        onToggleExpanded={(id) => toggleIntermediateMessages(id, 'after')}
      />
    )), [afterMessages, expandedMessages] // Убрали toggleIntermediateMessages из зависимостей
  );

  return (
    <section className="chat-comparison">
      <div className="chat-container">
        <div className="chat-header">
          <h3 className="chat-title">До оптимизации</h3>
          <div className="chat-header-actions">
            <button 
              className="clear-chat-btn"
              onClick={handleClearBeforeChat}
              title="Очистить сообщения"
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>
        <div className="chat-box before-chat">
          <div className="chat-messages" ref={beforeChatRef}>
            {beforeMessagesElements}
            {isLoadingBefore && (
              <div className="chat-message bot-message">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>
          <div className="chat-input-container">
            <textarea
              ref={beforeInputRef}
              rows={1}
              className="chat-input"
              placeholder={coordinatorPrompt ? "Ваше сообщение…" : "Сначала введите промпт"}
              value={beforeInput}
              onChange={(e) => setMultiAgentChatInput('before', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendBefore();
                }
              }}
              disabled={!coordinatorPrompt}
            />
            <button 
              className="send-button" 
              onClick={handleSendBefore}
              disabled={isLoadingBefore || !coordinatorPrompt}
            >
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
      <div className="chat-container">
        <div className="chat-header">
          <h3 className="chat-title">После оптимизации</h3>
          <div className="chat-header-actions">
            <button 
              className="clear-chat-btn"
              onClick={handleClearAfterChat}
              title="Очистить сообщения"
            >
              <i className="fas fa-trash"></i>
            </button>
            {optimizedIterationResults.length > 1 && (
              <div className="model-selector-container">
                <button 
                  className="model-selector-btn"
                  onClick={() => setShowModelSelector(!showModelSelector)}
                >
                  {multiAgentSelectedIterationIndex + 1}
                  <i className={`fas fa-chevron-${showModelSelector ? 'up' : 'down'}`}></i>
                </button>
                {showModelSelector && (
                  <div className="model-dropdown">
                    {optimizedIterationResults.map((_, index) => (
                      <button
                        key={index}
                        className={`model-option ${multiAgentSelectedIterationIndex === index ? 'active' : ''}`}
                        onClick={() => handleModelSelect(index)}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={`chat-box after-chat ${!hasIterationResults ? 'disabled' : ''}`}>
          <div className="chat-messages" ref={afterChatRef}>
            {afterMessagesElements}
            {isLoadingAfter && (
              <div className="chat-message bot-message">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>
          <div className="chat-input-container">
            <textarea
              ref={afterInputRef}
              rows={1}
              className="chat-input"
              placeholder={hasIterationResults ? "Ваше сообщение…" : "Сначала завершите оптимизацию"}
              value={afterInput}
              onChange={(e) => setMultiAgentChatInput('after', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendAfter();
                }
              }}
              disabled={!hasIterationResults}
            />
            <button 
              className="send-button" 
              onClick={handleSendAfter}
              disabled={isLoadingAfter || !hasIterationResults}
            >
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MultiAgentChatComparison;
